import React from 'react';
import clx from 'classnames';
import { FormattedMessage } from 'react-intl';
import { useField } from 'react-final-form';

import { useSelector } from 'hooks';
import { getSelectedSelfServiceId } from 'modules/auth/selectors';
import { getAutoValidationWorkflow, getMobilityAutomatedValidationWorkflowType } from 'modules/selfServices/selectors';
import { isPxlCheckEligible } from 'modules/selfServices/utils';
import { useStartAutomatedValidationMutation, useGetAutomatedValidationQuery } from 'modules/selfServices/service';
import { ExclamationTriangleIcon as WarningIcon } from '@heroicons/react/24/solid';
import {
  AutomatedValidationStatus,
  MobilityDocument,
} from 'modules/selfServices/types/SelfService';

import useLabelTranslation from 'hooks/useLabelTranslation';

import Link from 'components/ui/Link';
import Carousel from 'components/ui/Carousel';
import { getUrlParam } from 'utils/urlUtils';
import { Icon, Image, Spinner } from 'components/ui';
import MandatorySymbol from 'components/ui/MandatorySymbol';
import { getSelectedLanguage } from 'modules/translations/selectors';
import { AbstractMedia } from 'types/AbstractMedia';
import Bowser from 'bowser';


type AutomatedDocumentValidatorProps = {
  name: string;
};

const POLLING_RATE = 10000;
const UNSUCCESSFUL_STATUS = [AutomatedValidationStatus.DOCUMENT_SCAN_UNSUCCESSFUL, AutomatedValidationStatus.SELFIE_SCAN_UNSUCCESSFUL];

const getIcon = (isLoading: boolean, completed: boolean) => {
  if (isLoading) {
    return <Spinner />;
  }
  return <Icon className={clx({
    'fb-icon--arrow-line-left inline-block rotate-135':!completed,
    'fb-icon--check': completed,
  }, 'text-2xl kiosk:text-4xl text-default justify-end')} />;
};


const AutomatedDocumentValidator: React.FC<AutomatedDocumentValidatorProps> = ({ name }) => {
  const { getLabelTranslation } = useLabelTranslation();
  const platformType = Bowser.getParser(window.navigator.userAgent).getPlatformType();
  const isMobile = platformType === 'mobile';

  const { value: document } = useField<MobilityDocument>(name, { subscription: { value: true } }).input;
  const { status } = document;
  const isEligibleForCheck = React.useMemo(() => isPxlCheckEligible(status), [status]);

  const { input: { value: validationStatus, onChange: onValidationStatusChange }, meta } = useField<AutomatedValidationStatus>(
    `${name}.validationStatus`,
    {
      subscription: { value: true, error: true, submitFailed: true },
      validate: (value) => (!value || value === AutomatedValidationStatus.ACTIVE) && isEligibleForCheck
        ? 'validation.required'
        : undefined,
    },
  );

  const error = meta.submitFailed && meta.error && <FormattedMessage id={meta.error} defaultMessage={meta.error}/>;

  const { input: { onChange: onMediasChange } } = useField(`${name}.medias`, {
    format: (v) => v,
    subscription: {},
  });

  const token = getUrlParam('token');
  const locale = useSelector(getSelectedLanguage);
  const selfServiceId = useSelector(getSelectedSelfServiceId);
  const automatedDocumentsValidation = useSelector(getAutoValidationWorkflow);
  const workflowType = useSelector(getMobilityAutomatedValidationWorkflowType);

  const [mediasFullscreen, setMediasFullscreen] = React.useState<AbstractMedia[]>([]);

  const processIsCompleted = Boolean(validationStatus) && validationStatus !== AutomatedValidationStatus.ACTIVE;
  const failedAttempt = UNSUCCESSFUL_STATUS.includes(validationStatus);

  const [startValidation, { data, isLoading }] = useStartAutomatedValidationMutation();

  React.useEffect(() => {
    if (!processIsCompleted && isEligibleForCheck && selfServiceId && workflowType) {
      startValidation({ selfServiceId, workflowType });
    }
  // only needed on mount, needed since we're using a mutation
  // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, []);

  const workflowId = data?.id ?? automatedDocumentsValidation?.id;
  const isPollingActive = selfServiceId && workflowId && (!validationStatus || validationStatus === AutomatedValidationStatus.ACTIVE);

  const { data: autoValidationData } = useGetAutomatedValidationQuery(
    { selfServiceId },
    { skip: !selfServiceId, pollingInterval: isPollingActive ? POLLING_RATE : 0 }
  );

  const polledDocument = autoValidationData?.mobility?.documents[0];

  React.useEffect(() => {
    const status = autoValidationData?.validationStatus;
    if (status && status !== validationStatus) {
      onValidationStatusChange(status);
    }
  }, [autoValidationData?.validationStatus, onValidationStatusChange, validationStatus]);


  React.useEffect(() => {
    const polledMedias = polledDocument?.medias;
    if (polledMedias?.length) {
      onMediasChange(polledMedias);
    }
  }, [polledDocument?.medias, onMediasChange]);

  const medias = React.useMemo(
    () => polledDocument?.medias ?? document.medias ?? [],
    [polledDocument, document],
  );

  const getUrl = React.useMemo(() => {
    if (isLoading || !data?.url || !token || processIsCompleted) {
      return;
    }
    const urlObj = new URL(data.url);
    urlObj.searchParams.set('oi', token);
    urlObj.searchParams.set('lang', locale?.split('_')[0]);
    return urlObj.toString();
  }, [data?.url, isLoading, token, locale, processIsCompleted]);

  const handleOpenCarousel = React.useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    setMediasFullscreen(medias);
  }, [medias]);

  const handleCloseCarousel = React.useCallback(() => {
    setMediasFullscreen([]);
  }, []);

  const displayCarousel = mediasFullscreen.length > 0;

  const mediaCount = medias.length;
  const previewPath = mediaCount > 0 ? medias[0].path : null;

  return (
    <>
      <p className="text-center mb-l" data-testid="automated-validator-description">
        <FormattedMessage
          id="mobilityDocument.automated"
          defaultMessage="Verify your driver’s license through our secure partner. It only takes a few minutes."
        />
      </p>
      <div className="w-full rounded-card border border-border-default mb-l">
        {displayCarousel && (
          <Carousel
            fullscreen
            medias={mediasFullscreen}
            onClose={handleCloseCarousel}
            hideActions
          />
        )}
        <Link
          href={getUrl}
          target={isMobile ? '_self': '_blank'}
          className="no-underline text-low flex flex-row items-center justify-between p-6"
        >
          <div className="flex flex-auto items-center relative">
            {mediaCount > 0 && <div
              onClick={handleOpenCarousel}
              className="mr-m size-2xl rounded-sm cursor-pointer relative overflow-hidden flex items-center justify-center"
              role="button"
              tabIndex={0}
            >
              {previewPath && <Image {...medias[0]} thumbnail src={previewPath} className="size-full bg-cover" />}
              {mediaCount > 1 && (
                <span className="absolute bg-white rounded-full text-primary text-xs leading-none px-1 py-0.5 z-50">
                  {mediaCount}
                </span>
              )}
            </div>}
            <div className="flex flex-col">
              <div>
                <MandatorySymbol className="mr-1" />{getLabelTranslation(document.name)}
              </div>

              {error && (
                <span className="text-danger-high text-xxs! font-normal block">
                  {error}
                </span>
              )}
            </div>
          </div>
          {getIcon(isLoading, processIsCompleted)}
        </Link>
      </div>
      {failedAttempt &&
        <div className="warning-alert mt-xs rounded-card" data-testid="automated-validator-warning">
          <div className="mr-xs">
            <WarningIcon className="size-l text-warning-default" />
          </div>
          <span className="text-xs!">
            <FormattedMessage
              id="mobilityDocument.warning"

              defaultMessage="Verification system was not able to validate your document. Dealership will process to an internal verification and will get back to you if needed. Please proceed to the following steps of your Check-in. "
            />
          </span>
        </div>
      }
    </>
  );
};

export default AutomatedDocumentValidator;